import React from 'react';
import { useRouter } from 'next/navigation';

export interface CourseOffer {
  id: string;
  title: string;
  level: string;
  duration: string;
  schedule: string;
  slug?: string;
  buttonVariant?: 'outline' | 'filled';
  buttonColor?: 'green' | 'gray';
  iconColor?: 'blue' | 'green' | 'salmon';
}

interface CourseCardProps {
  course: CourseOffer;
  applyButtonText: string;
}

const CalendarIcon = ({ color = '#F05A49' }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.25 0V1.5H9.75V0H11.25V1.5H14.25C14.6642 1.5 15 1.83579 15 2.25V14.25C15 14.6642 14.6642 15 14.25 15H0.75C0.33579 15 0 14.6642 0 14.25V2.25C0 1.83579 0.33579 1.5 0.75 1.5H3.75V0H5.25ZM13.5 7.5H1.5V13.5H13.5V7.5ZM4.5 9V10.5H3V9H4.5ZM8.25 9V10.5H6.75V9H8.25ZM12 9V10.5H10.5V9H12ZM3.75 3H1.5V6H13.5V3H11.25V4.5H9.75V3H5.25V4.5H3.75V3Z" fill={color}/>
  </svg>
);

const ClockIcon = ({ color = '#F05A49' }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.5 15C3.35786 15 0 11.6421 0 7.5C0 3.35786 3.35786 0 7.5 0C11.6421 0 15 3.35786 15 7.5C15 11.6421 11.6421 15 7.5 15ZM7.5 13.5C10.8137 13.5 13.5 10.8137 13.5 7.5C13.5 4.18629 10.8137 1.5 7.5 1.5C4.18629 1.5 1.5 4.18629 1.5 7.5C1.5 10.8137 4.18629 13.5 7.5 13.5ZM8.25 7.5H11.25V9H6.75V3.75H8.25V7.5Z" fill={color}/>
  </svg>
);

export const CourseCard: React.FC<CourseCardProps> = ({ course, applyButtonText }) => {
  const router = useRouter();
  const buttonClass = 'bg-white text-[#82B662] border border-[#82B662]';

  const iconCircleClass = 'border-2 border-[#377396] text-[#377396]';

  const handleViewDetails = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('View Details clicked', { slug: course.slug, course });
    if (course.slug) {
      router.push(`/ofertas-formativas/${course.slug}`);
    } else {
      console.warn('Slug não disponível para esta oferta');
    }
  };

  const handleApply = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ofertasUrl = process.env.NEXT_PUBLIC_OFERTAS_URL;
    console.log('Apply clicked', { slug: course.slug, ofertasUrl, course });
    if (course.slug && ofertasUrl) {
      window.open(`${ofertasUrl}/${course.slug}`, '_blank');
    } else {
      console.warn('Slug ou OFERTAS_URL não disponível', { slug: course.slug, ofertasUrl });
    }
  };

  return (
    <div className="w-full px-3 sm:px-4 py-4 sm:py-5 md:py-6 bg-white rounded-2xl md:rounded-3xl shadow-[0px_8px_20px_0px_rgba(131,130,130,0.15)] flex flex-col gap-6 sm:gap-8 md:gap-10">
      <div className="flex flex-col gap-3 sm:gap-4">
        <h3 className="text-zinc-700 text-base sm:text-lg font-medium font-['Poppins'] leading-6 sm:leading-7 line-clamp-3 min-h-[72px] sm:min-h-[84px]">
          {course.title}
        </h3>

        <div className="text-[#377396] text-base sm:text-lg font-normal font-['Poppins'] uppercase">
         Nível {course.level}
        </div>

        <div className="flex flex-wrap w-full justify-start items-center min-h-[32px] gap-4 sm:gap-6 md:gap-10">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <CalendarIcon />
            <span className="text-zinc-600 text-sm sm:text-base font-light font-['Poppins'] leading-normal sm:leading-loose">
              {course.duration} meses
            </span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <ClockIcon />
            <span className="text-zinc-600 text-sm sm:text-base font-light font-['Poppins'] leading-normal sm:leading-loose">
              {course.schedule}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <button
          type="button"
          onClick={handleApply}
          className={`px-4 sm:px-6 py-1.5 rounded-[30px] text-sm sm:text-base font-medium font-['Rubik'] leading-loose tracking-wide transition-colors ${buttonClass} hover:bg-[#82B662] hover:text-white cursor-pointer`}
          aria-label="Candidatar a esta oferta"
        >
          {applyButtonText}
        </button>

        <button
          type="button"
          onClick={handleViewDetails}
          className={`w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center ${iconCircleClass} transition-all hover:scale-110 hover:bg-[#377396] hover:text-white cursor-pointer`}
          aria-label="Ver detalhes da oferta"
        >
          <span className="text-xl sm:text-2xl font-normal leading-9">+</span>
        </button>
      </div>
    </div>
  );
};
